<template>
  <div class="purchase-order-list-container">
    <PurchaseOrderFilters :suppliers="suppliers" :transporters="transporters" />

    <div
      v-if="orders.length === 0 && loaded && hasActiveFilters"
      class="has-text-centered is-size-5 is-italic mt-6"
    >
      <p>No purchase orders matched your search/filters.</p>
      <p><a @click.prevent="store.resetSearch">Clear filters</a></p>
    </div>

    <div v-else-if="orders.length === 0 && loaded" class="has-text-centered empty-state">
      <h2 class="subtitle">No purchase orders yet</h2>
    </div>

    <table v-else class="table is-fullwidth is-striped purchase-order-list">
      <thead>
        <tr>
          <SortableHeader :store="store" field="visual_id">Order ID</SortableHeader>
          <th>State</th>
          <th>Supplier</th>
          <th>Transporter</th>
          <SortableHeader :store="store" field="estimated_arrival_at">
            Est. Arrival
          </SortableHeader>
          <SortableHeader :store="store" field="received_at">Received</SortableHeader>
          <th></th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="order in orders" :key="order.id">
          <td>
            <div class="id">
              <a :href="orderPath(order)">{{ order.visual_id }}</a>
            </div>
          </td>
          <td class="state"><PurchaseOrderState :state="order.state" /></td>
          <td class="supplier">{{ order.supplier?.name ?? "—" }}</td>
          <td class="transporter">{{ order.transporter?.name ?? "—" }}</td>
          <td class="arrival">{{ formatDate(order.estimated_arrival_at) }}</td>
          <td class="received">{{ formatDate(order.received_at) }}</td>
          <td class="col-actions">
            <PurchaseOrderActionsButton :order="order" :confirms="confirms" />
          </td>
        </tr>
      </tbody>
    </table>

    <PagerWidget
      v-if="totalEntries > perPage"
      v-model="currentPage"
      :total-entries="totalEntries"
      :per-page="perPage"
      @update:model-value="store.loadPage($event)"
    />
  </div>
</template>

<script setup>
  import { storeToRefs } from "pinia";
  import { format, parseISO } from "date-fns";
  import PagerWidget from "./pager-widget.vue";
  import PurchaseOrderFilters from "./purchase-order-filters.vue";
  import PurchaseOrderState from "./purchase-order-state.vue";
  import PurchaseOrderActionsButton from "./purchase-order-actions-button.vue";
  import SortableHeader from "./shared/sortable-header.vue";
  import { usePurchaseOrderSearchStore } from "./stores/purchase-order-search-store";
  import { useSearchList } from "./lib/use-search-list";
  import { editPurchaseOrderPath, purchaseOrderPath } from "~/routes";

  const props = defineProps({
    userId: { type: Number, required: true },
    suppliers: { type: Array, default: () => [] },
    transporters: { type: Array, default: () => [] },
    initialData: { type: Object, default: null },
    sortConfig: { type: Object, required: true },
    confirms: { type: Object, required: true },
  });

  const store = usePurchaseOrderSearchStore();
  const { orders, totalEntries, currentPage, perPage, loaded, hasActiveFilters } =
    storeToRefs(store);

  useSearchList(store, {
    userId: props.userId,
    sortConfig: props.sortConfig,
    initialData: props.initialData,
    knownIds: {
      supplierIds: props.suppliers.map(({ value }) => value),
      transporterIds: props.transporters.map(({ value }) => value),
    },
  });

  function orderPath(order) {
    return order.state === "draft" ? editPurchaseOrderPath(order) : purchaseOrderPath(order);
  }

  function formatDate(dateStr) {
    if (!dateStr) return "—";
    return format(parseISO(dateStr), "MMM d, yyyy");
  }
</script>

<style scoped lang="scss">
  @import "~jose_modern/css";
  @import "~jose_modern/css/inventory/mixins";

  .purchase-order-list-container {
    display: flex;
    flex-direction: column;
  }

  .empty-state {
    margin-top: 2rem;
  }

  table {
    .id {
      display: inline-flex;
      width: 100%;

      a {
        flex-grow: 1;
      }
    }

    tbody tr td {
      vertical-align: middle;
    }

    @include until(860px) {
      display: block;

      thead {
        display: none;
      }

      tbody {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
      }

      tbody tr {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.4rem 1rem;
        border: 1px solid $border;
        border-radius: $radius;
        padding: 0.75rem;
        background-color: $white !important;
        @include soft-shadow;

        td {
          // Some inventory classes set the width to 1% with very specific rules
          width: auto !important;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          border: none;
          padding: 0.1rem 0;

          &:nth-child(1) {
            font-weight: $weight-semibold;
            width: 80% !important;
            flex-grow: 1;
            a {
              width: 100%;
            }
          }

          &:nth-child(n + 3) {
            flex-basis: 45%;
          }

          &.state {
            @media screen and (max-width: 540px) {
              flex-basis: 100%;
            }
          }

          &::before {
            color: $grey;
            font-size: 0.8em;
          }

          &.supplier::before {
            content: "Supplier";
          }

          &.transporter::before {
            content: "Transporter";
          }

          &.arrival::before {
            content: "Est. Arrival";
          }

          &.received::before {
            content: "Received";
          }
        }
      }
    }
  }
</style>
